import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, Input, Type, ViewChild, inject, DOCUMENT, OnDestroy, PLATFORM_ID, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { WhiteLabelHeaderLoader } from './whitelabel/white-label-header-loader';
import { RASMUS_BRANDING } from './constants/branding';
import { QUERY_PARAMS } from './constants/query-params';
import { AuctionService, AuthService, ErrorService, FirebaseService, UserService } from './services';
import { consumeSignalEvents } from './services/signal-event';
import { IUser } from './interfaces';
import { LotViewTransitionService } from './services/lot-view-transition.service';
import { User } from '@angular/fire/auth';
// Imported from their concrete files (NOT the ./users/profile barrel): these are
// only used inside @defer blocks, and a barrel import makes the bundler keep all
// exports together, which defeats the deferred loading.
import { ProfileNotificationsComponent } from './users/profile/profile-notifications.component';
import { ProfilePersonalInfoComponent } from './users/profile/profile-personal-info.component';
import { ProfilePaymentMethodsComponent } from './users/profile/profile-payment-methods.component';
import { ProfileChangePasswordComponent } from './users/profile/profile-change-password.component';
import { ProfileInvoicesComponent } from './users/profile/profile-invoices.component';
import { CreditsComponent } from './users/credits.component';
import { shouldLog } from './utils/logging';

type RecentSearchType = 'text' | 'category';
type MobileAccountView = 'main' | 'notifications' | 'personal' | 'password' | 'payments' | 'invoices' | 'credits';

export const MOBILE_DRAWER_RESPONSE_MS = 0;

/**
 * Represents a stored recent search entry for the header search history.
 */
interface RecentSearchEntry {
  type: RecentSearchType;
  value: string;
}

@Component({
    selector: 'app-header',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      CommonModule,
      RouterModule,
      FormsModule,
      MatMenuModule,
      ButtonModule,
      InputTextModule,
      DrawerModule,
      MatProgressSpinnerModule,
      ProfileNotificationsComponent,
      ProfilePersonalInfoComponent,
      ProfileChangePasswordComponent,
      ProfilePaymentMethodsComponent,
      ProfileInvoicesComponent,
      CreditsComponent,
    ],
    styleUrls: ['./header.component.scss'],
    template: `
    @if (whiteLabelComponent; as whiteLabel) {
      <ng-container *ngComponentOutlet="whiteLabel"></ng-container>
    } @else {
      <div class="rasmus-shell">
        <div class="rasmus-top">
          <button
            type="button"
            class="menu-button"
            aria-label="Open menu"
            [attr.aria-expanded]="mobileMenuOpen"
            aria-controls="mobile-menu-drawer"
            (click)="openMobileMenu()"
            >
            <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
              <path d="M3.625 21.75V19.3333H25.375V21.75H3.625ZM3.625 15.7083V13.2917H25.375V15.7083H3.625ZM3.625 9.66667V7.25H25.375V9.66667H3.625Z" fill="#020617"/>
            </svg>
          </button>
          <a class="rasmus-logo" routerLink="/" href="/" [attr.aria-label]="brand.ariaLabel" (click)="onLogoClick($event)">
            <img
              class="rasmus-logo__brand rasmus-logo__brand--desktop"
              [src]="brand.primary"
              [attr.alt]="brand.ariaLabel"
              width="155"
              height="19"
              />
            <img
              class="rasmus-logo__brand rasmus-logo__brand--mobile"
              [src]="brand.mark"
              [attr.alt]="brand.ariaLabel"
              width="47"
              height="31"
              />
          </a>
          <div class="rasmus-search-wrapper">
            <form class="rasmus-search" (submit)="onSearchSubmit($event)">
              <span class="search-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                  <g clip-path="url(#clip0_3042_10656)">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.62857 8.16457C1.62857 4.55484 4.55483 1.62857 8.16456 1.62857C11.7743 1.62857 14.7006 4.55484 14.7006 8.16457C14.7006 11.7743 11.7743 14.7006 8.16456 14.7006C4.55483 14.7006 1.62857 11.7743 1.62857 8.16457ZM8.16456 0C3.65539 0 0 3.65541 0 8.16457C0 12.6737 3.65539 16.3291 8.16456 16.3291C10.1259 16.3291 11.9257 15.6376 13.3334 14.485L17.6099 18.7615C17.9279 19.0795 18.4435 19.0795 18.7615 18.7615C19.0795 18.4435 19.0795 17.9279 18.7615 17.6099L14.485 13.3334C15.6376 11.9257 16.3292 10.1259 16.3292 8.16457C16.3292 3.6554 12.6737 0 8.16456 0Z" fill="#64748B"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_3042_10656">
                      <rect width="19" height="19" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </span>
              <input
                pInputText
                [(ngModel)]="searchTerm"
                name="headerSearch"
                placeholder="Find anything..."
                autocomplete="off"
                aria-label="Search auctions and items"
                (focus)="handleSearchFocus()"
                (click)="handleSearchFocus()"
                (blur)="closeAutocomplete()"
                (input)="onSearchInput()"
                />
              <button
                type="submit"
                pButton
                class="search-button"
                icon="pi pi-search"
                aria-label="Submit search"
              ></button>
            </form>
            @if (autocompleteOpen && shouldShowAutocomplete) {
              <div class="search-autocomplete" role="listbox" aria-label="Search suggestions">
                <ng-container *ngTemplateOutlet="autocompleteList"></ng-container>
              </div>
            }
          </div>
          @if (mobileSearchOpen) {
            <div class="mobile-search-overlay" role="dialog" aria-modal="true" aria-label="Search">
              <div class="mobile-search-overlay__header">
                <button type="button" class="mobile-search-overlay__close" (click)="closeMobileSearch()">
                  <svg class="mobile-search-overlay__close-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.90769 2.44538C3.50395 2.04164 2.84936 2.04164 2.44563 2.44538C2.04189 2.84912 2.04189 3.50371 2.44563 3.90744L8.5379 9.99972L2.44563 16.092C2.04189 16.4957 2.04189 17.1503 2.44563 17.5541C2.84936 17.9578 3.50395 17.9578 3.90769 17.5541L9.99997 11.4618L16.0922 17.5541C16.496 17.9578 17.1506 17.9578 17.5543 17.5541C17.958 17.1503 17.958 16.4957 17.5543 16.092L11.462 9.99972L17.5543 3.90744C17.958 3.50371 17.958 2.84912 17.5543 2.44538C17.1506 2.04164 16.496 2.04164 16.0922 2.44538L9.99997 8.53766L3.90769 2.44538Z" fill="black"/>
                  </svg>
                  <span class="sr-only">Close search</span>
                </button>
              </div>
              <form class="mobile-search-overlay__form" (submit)="onSearchSubmit($event)">
                <span class="mobile-search-overlay__icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 19 19" fill="none">
                    <g clip-path="url(#clip0_3042_10656)">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M1.62857 8.16457C1.62857 4.55484 4.55483 1.62857 8.16456 1.62857C11.7743 1.62857 14.7006 4.55484 14.7006 8.16457C14.7006 11.7743 11.7743 14.7006 8.16456 14.7006C4.55483 14.7006 1.62857 11.7743 1.62857 8.16457ZM8.16456 0C3.65539 0 0 3.65541 0 8.16457C0 12.6737 3.65539 16.3291 8.16456 16.3291C10.1259 16.3291 11.9257 15.6376 13.3334 14.485L17.6099 18.7615C17.9279 19.0795 18.4435 19.0795 18.7615 18.7615C19.0795 18.4435 19.0795 17.9279 18.7615 17.6099L14.485 13.3334C15.6376 11.9257 16.3292 10.1259 16.3292 8.16457C16.3292 3.6554 12.6737 0 8.16456 0Z" fill="#94A3B8"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_3042_10656">
                        <rect width="19" height="19" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                </span>
                <input
                  pInputText
                  [(ngModel)]="searchTerm"
                  name="mobileHeaderSearch"
                  placeholder="Find anything..."
                  autocomplete="off"
                  aria-label="Search auctions and items"
                  (input)="onSearchInput()"
                  />
              </form>
              @if (shouldShowAutocomplete) {
                <div class="search-autocomplete search-autocomplete--mobile" role="listbox" aria-label="Search suggestions">
                  <ng-container *ngTemplateOutlet="autocompleteList"></ng-container>
                </div>
              }
            </div>
          }
          <ng-template #autocompleteList>
            <div class="search-autocomplete__list">
              @if (filteredSearchHistory.length > 0) {
                <span class="search-autocomplete__section-title">Recent searches</span>
                @for (term of filteredSearchHistory; track trackByHistoryEntry($index, term)) {
                  <button
                    type="button"
                    class="search-autocomplete__item"
                    (mousedown)="keepAutocompleteOpen($event)"
                    (click)="applyHistoryTerm(term)"
                    >
                    <span class="search-autocomplete__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <g clip-path="url(#clip0_4058_18454)">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M0.485437 0.0776367C0.753536 0.0776367 0.970873 0.294974 0.970873 0.563073V2.44607L1.74737 1.66958C3.86574 -0.448797 7.29284 -0.448797 9.41122 1.66958C11.5296 3.78796 11.5296 7.21507 9.41122 9.33345C7.29284 11.4518 3.86574 11.4518 1.74737 9.33345C1.55779 9.14388 1.55779 8.83651 1.74737 8.64694C1.93694 8.45737 2.2443 8.45737 2.43388 8.64694C4.1731 10.3862 6.98548 10.3862 8.72471 8.64694C10.4639 6.90771 10.4639 4.09532 8.72471 2.35609C6.98548 0.616864 4.1731 0.616864 2.43388 2.35609L1.6379 3.15207H3.55987C3.82797 3.15207 4.04531 3.36941 4.04531 3.63751C4.04531 3.9056 3.82797 4.12294 3.55987 4.12294H0.485437C0.217337 4.12294 0 3.9056 0 3.63751V0.563073C0 0.294974 0.217337 0.0776367 0.485437 0.0776367ZM5.98705 2.66663C5.98705 2.39853 5.76971 2.1812 5.50162 2.1812C5.23352 2.1812 5.01618 2.39853 5.01618 2.66663V5.25563C5.01618 5.38437 5.06732 5.50785 5.15836 5.59888L6.77648 7.21701C6.96606 7.40658 7.27342 7.40658 7.46299 7.21701C7.65257 7.02743 7.65257 6.72007 7.46299 6.53049L5.98705 5.05455V2.66663Z" fill="#94A3B8"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_4058_18454">
                            <rect width="11" height="11" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                    <span class="search-autocomplete__label">{{ term.value }}</span>
                  </button>
                }
              }
              @if (hasTypedSearch && filteredAuctionTitles.length > 0) {
                <span class="search-autocomplete__section-title">Auctions</span>
                @for (title of filteredAuctionTitles; track title) {
                  <button
                    type="button"
                    class="search-autocomplete__item"
                    (mousedown)="keepAutocompleteOpen($event)"
                    (click)="applyTextSearch(title)"
                    >
                    <span class="search-autocomplete__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <g clip-path="url(#clip0_4058_18458)">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M0.942857 4.72686C0.942857 2.63701 2.63701 0.942857 4.72685 0.942857C6.8167 0.942857 8.51086 2.63701 8.51086 4.72686C8.51086 6.8167 6.8167 8.51086 4.72685 8.51086C2.63701 8.51086 0.942857 6.81671 0.942857 4.72686ZM4.72685 0C2.11628 0 0 2.11629 0 4.72686C0 7.33743 2.11628 9.45372 4.72685 9.45372C5.86235 9.45372 6.90434 9.05333 7.71934 8.38604L10.1952 10.8619C10.3793 11.046 10.6778 11.046 10.8619 10.8619C11.046 10.6778 11.046 10.3793 10.8619 10.1952L8.38604 7.71934C9.05333 6.90434 9.45372 5.86236 9.45372 4.72686C9.45372 2.11629 7.33742 0 4.72685 0Z" fill="#94A3B8"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_4058_18458">
                            <rect width="11" height="11" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                    <span class="search-autocomplete__label">{{ title }}</span>
                  </button>
                }
              }
              @if (hasTypedSearch && filteredCategoryOptions.length > 0) {
                <span class="search-autocomplete__section-title">Categories</span>
                @for (option of filteredCategoryOptions; track option.value) {
                  <button
                    type="button"
                    class="search-autocomplete__item"
                    (mousedown)="keepAutocompleteOpen($event)"
                    (click)="applyCategorySelection(option.value)"
                    >
                    <span class="search-autocomplete__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <g clip-path="url(#clip0_4058_18462)">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M3.61429 2.82857C3.61429 1.78779 4.45922 0.942857 5.5 0.942857C6.54078 0.942857 7.38571 1.78779 7.38571 2.82857V3.14286H3.61429V2.82857ZM2.67143 4.08571V5.5C2.67143 5.76036 2.88249 5.97143 3.14286 5.97143C3.40322 5.97143 3.61429 5.76036 3.61429 5.5V4.08571H7.38571V5.5C7.38571 5.76036 7.59678 5.97143 7.85714 5.97143C8.11751 5.97143 8.32857 5.76036 8.32857 5.5V4.08571H10.0571V9.27143C10.0571 9.70249 9.70249 10.0571 9.27143 10.0571H1.72857C1.29751 10.0571 0.942857 9.70249 0.942857 9.27143V4.08571H2.67143ZM2.67143 3.14286V2.82857C2.67143 1.26707 3.93849 0 5.5 0C7.06151 0 8.32857 1.26707 8.32857 2.82857V3.14286H10.2143C10.6506 3.14286 11 3.49221 11 3.92857V9.27143C11 10.2232 10.2232 11 9.27143 11H1.72857C0.77678 11 0 10.2232 0 9.27143V3.92857C0 3.49221 0.349351 3.14286 0.785714 3.14286H2.67143Z" fill="#94A3B8"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_4058_18462">
                            <rect width="11" height="11" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                    <span class="search-autocomplete__label">{{ option.label }}</span>
                  </button>
                }
              }
            </div>
          </ng-template>
          <div class="rasmus-account">
            @if (!loggedIn && authLoaded) {
              <button
                type="button"
                pButton
                class="sign-in-button"
                (click)="onSignInClick()"
                >
                Sign In
              </button>
            }
            @if (loggedIn) {
              @if (isMobileViewport()) {
                <button
                  type="button"
                  pButton
                  class="account-button"
                  (click)="openMobileAccountMenu()"
                >
                  <svg
                    class="account-button__icon"
                    width="29"
                    height="29"
                    viewBox="0 0 29 29"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <mask id="account-mask" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="29" height="29">
                      <rect width="29" height="29" fill="#D9D9D9"/>
                    </mask>
                    <g mask="url(#account-mask)">
                      <path
                        d="M7.06879 20.6623C8.09588 19.8769 9.24379 19.2577 10.5125 18.8045C11.7813 18.3514 13.1105 18.1248 14.5 18.1248C15.8896 18.1248 17.2188 18.3514 18.4875 18.8045C19.7563 19.2577 20.9042 19.8769 21.9313 20.6623C22.6362 19.8366 23.1849 18.9002 23.5776 17.853C23.9704 16.8057 24.1667 15.688 24.1667 14.4998C24.1667 11.8214 23.2252 9.54064 21.3422 7.65765C19.4592 5.77466 17.1785 4.83317 14.5 4.83317C11.8216 4.83317 9.54084 5.77466 7.65786 7.65765C5.77487 9.54064 4.83338 11.8214 4.83338 14.4998C4.83338 15.688 5.02973 16.8057 5.42244 17.853C5.81515 18.9002 6.36393 19.8366 7.06879 20.6623ZM14.5 15.7082C13.3118 15.7082 12.3099 15.3004 11.4943 14.4847C10.6787 13.6691 10.2709 12.6672 10.2709 11.479C10.2709 10.2908 10.6787 9.2889 11.4943 8.47328C12.3099 7.65765 13.3118 7.24984 14.5 7.24984C15.6882 7.24984 16.6901 7.65765 17.5058 8.47328C18.3214 9.2889 18.7292 10.2908 18.7292 11.479C18.7292 12.6672 18.3214 13.6691 17.5058 14.4847C16.6901 15.3004 15.6882 15.7082 14.5 15.7082ZM14.5 26.5832C12.8285 26.5832 11.2577 26.266 9.78754 25.6316C8.3174 24.9972 7.03858 24.1363 5.95108 23.0488C4.86358 21.9613 4.00265 20.6825 3.36827 19.2123C2.7339 17.7422 2.41671 16.1714 2.41671 14.4998C2.41671 12.8283 2.7339 11.2575 3.36827 9.78734C4.00265 8.3172 4.86358 7.03838 5.95108 5.95088C7.03858 4.86338 8.3174 4.00244 9.78754 3.36807C11.2577 2.73369 12.8285 2.4165 14.5 2.4165C16.1716 2.4165 17.7424 2.73369 19.2125 3.36807C20.6827 4.00244 21.9615 4.86338 23.049 5.95088C24.1365 7.03838 24.9974 8.3172 25.6318 9.78734C26.2662 11.2575 26.5834 12.8283 26.5834 14.4998C26.5834 16.1714 26.2662 17.7422 25.6318 19.2123C24.9974 20.6825 24.1365 21.9613 23.049 23.0488C21.9615 24.1363 20.6827 24.9972 19.2125 25.6316C17.7424 26.266 16.1716 26.5832 14.5 26.5832Z"
                        fill="#020617"
                      />
                    </g>
                  </svg>
                  <span class="user-name">{{ userName || 'Account' }}</span>
                  <svg class="chevron" xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0.21967 0.21967C0.512563 -0.0732233 0.987437 -0.0732233 1.28033 0.21967L5.75 4.68934L10.2197 0.21967C10.5126 -0.0732233 10.9874 -0.0732233 11.2803 0.21967C11.5732 0.512563 11.5732 0.987437 11.2803 1.28033L6.28033 6.28033C5.98744 6.57322 5.51256 6.57322 5.21967 6.28033L0.21967 1.28033C-0.0732233 0.987437 -0.0732233 0.512563 0.21967 0.21967Z" fill="#020617"/>
                  </svg>
                </button>
              } @else {
                <button
                  type="button"
                  pButton
                  class="account-button"
                  [matMenuTriggerFor]="userMenu"
                >
                  <svg
                    class="account-button__icon"
                    width="29"
                    height="29"
                    viewBox="0 0 29 29"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <mask id="account-mask" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="29" height="29">
                      <rect width="29" height="29" fill="#D9D9D9"/>
                    </mask>
                    <g mask="url(#account-mask)">
                      <path
                        d="M7.06879 20.6623C8.09588 19.8769 9.24379 19.2577 10.5125 18.8045C11.7813 18.3514 13.1105 18.1248 14.5 18.1248C15.8896 18.1248 17.2188 18.3514 18.4875 18.8045C19.7563 19.2577 20.9042 19.8769 21.9313 20.6623C22.6362 19.8366 23.1849 18.9002 23.5776 17.853C23.9704 16.8057 24.1667 15.688 24.1667 14.4998C24.1667 11.8214 23.2252 9.54064 21.3422 7.65765C19.4592 5.77466 17.1785 4.83317 14.5 4.83317C11.8216 4.83317 9.54084 5.77466 7.65786 7.65765C5.77487 9.54064 4.83338 11.8214 4.83338 14.4998C4.83338 15.688 5.02973 16.8057 5.42244 17.853C5.81515 18.9002 6.36393 19.8366 7.06879 20.6623ZM14.5 15.7082C13.3118 15.7082 12.3099 15.3004 11.4943 14.4847C10.6787 13.6691 10.2709 12.6672 10.2709 11.479C10.2709 10.2908 10.6787 9.2889 11.4943 8.47328C12.3099 7.65765 13.3118 7.24984 14.5 7.24984C15.6882 7.24984 16.6901 7.65765 17.5058 8.47328C18.3214 9.2889 18.7292 10.2908 18.7292 11.479C18.7292 12.6672 18.3214 13.6691 17.5058 14.4847C16.6901 15.3004 15.6882 15.7082 14.5 15.7082ZM14.5 26.5832C12.8285 26.5832 11.2577 26.266 9.78754 25.6316C8.3174 24.9972 7.03858 24.1363 5.95108 23.0488C4.86358 21.9613 4.00265 20.6825 3.36827 19.2123C2.7339 17.7422 2.41671 16.1714 2.41671 14.4998C2.41671 12.8283 2.7339 11.2575 3.36827 9.78734C4.00265 8.3172 4.86358 7.03838 5.95108 5.95088C7.03858 4.86338 8.3174 4.00244 9.78754 3.36807C11.2577 2.73369 12.8285 2.4165 14.5 2.4165C16.1716 2.4165 17.7424 2.73369 19.2125 3.36807C20.6827 4.00244 21.9615 4.86338 23.049 5.95088C24.1365 7.03838 24.9974 8.3172 25.6318 9.78734C26.2662 11.2575 26.5834 12.8283 26.5834 14.4998C26.5834 16.1714 26.2662 17.7422 25.6318 19.2123C24.9974 20.6825 24.1365 21.9613 23.049 23.0488C21.9615 24.1363 20.6827 24.9972 19.2125 25.6316C17.7424 26.266 16.1716 26.5832 14.5 26.5832Z"
                        fill="#020617"
                      />
                    </g>
                  </svg>
                  <span class="user-name">{{ userName || 'Account' }}</span>
                  <svg class="chevron" xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0.21967 0.21967C0.512563 -0.0732233 0.987437 -0.0732233 1.28033 0.21967L5.75 4.68934L10.2197 0.21967C10.5126 -0.0732233 10.9874 -0.0732233 11.2803 0.21967C11.5732 0.512563 11.5732 0.987437 11.2803 1.28033L6.28033 6.28033C5.98744 6.57322 5.51256 6.57322 5.21967 6.28033L0.21967 1.28033C-0.0732233 0.987437 -0.0732233 0.512563 0.21967 0.21967Z" fill="#020617"/>
                  </svg>
                </button>
                <mat-menu #userMenu="matMenu" class="account-menu">
                  <button mat-menu-item routerLink="/user/profile">Account settings</button>
                  <button mat-menu-item (click)="logout.emit()">Sign out</button>
                </mat-menu>
              }
            }
          </div>
        </div>
      </div>
      @if (mobileMenuOpen) {
        <button
          type="button"
          class="mobile-drawer-backdrop"
          aria-label="Close mobile menu"
          (click)="closeMobileMenu()"
        ></button>
      }
      @if (mobileAccountOpen) {
        <button
          type="button"
          class="mobile-drawer-backdrop"
          aria-label="Close account menu"
          (click)="closeMobileAccountMenu()"
        ></button>
      }
      <p-drawer
        id="mobile-menu-drawer"
        [(visible)]="mobileMenuOpen"
        position="left"
        [modal]="false"
        [dismissible]="false"
        [blockScroll]="false"
        [showCloseIcon]="false"
        [motionOptions]="mobileDrawerMotionOptions"
        styleClass="mobile-menu-drawer"
        (onHide)="handleMobileMenuHide()"
        >
        <ng-template pTemplate="headless">
          <div class="mobile-menu">
            @if (mobileMenuView === 'main') {
              <div class="mobile-menu__panel">
                @if (!loggedIn && authLoaded) {
                  <button type="button" class="mobile-menu__account" (click)="handleMobileAccountClick()">
                    <span class="mobile-menu__account-label">Sign In or create an account</span>
                  </button>
                  <span class="mobile-menu__divider" aria-hidden="true"></span>
                }
                @if (loggedIn) {
                  <a class="mobile-menu__account mobile-menu__account--signed-in" routerLink="/user/profile" (click)="closeMobileMenu()">
                    <span class="mobile-menu__account-label">Your Account</span>
                    <ng-container *ngTemplateOutlet="mobileMenuArrow"></ng-container>
                  </a>
                  <span class="mobile-menu__divider" aria-hidden="true"></span>
                }
                <button type="button" class="mobile-menu__link mobile-menu__link--category" (click)="openMobileCategoryMenu()">
                  <span>Shop by category</span>
                  <ng-container *ngTemplateOutlet="mobileMenuArrow"></ng-container>
                </button>
                <nav class="mobile-menu__nav" aria-label="Mobile menu">
                  @for (link of mobileMenuLinks; track link.label) {
                    @if (link.href) {
                      <a class="mobile-menu__link" [href]="link.href" (click)="closeMobileMenu()">
                        <span>{{ link.label }}</span>
                        <ng-container *ngTemplateOutlet="mobileMenuArrow"></ng-container>
                      </a>
                    } @else {
                      <a class="mobile-menu__link" [routerLink]="link.routerLink" (click)="closeMobileMenu()">
                        <span>{{ link.label }}</span>
                        <ng-container *ngTemplateOutlet="mobileMenuArrow"></ng-container>
                      </a>
                    }
                    @if (link.divider) {
                      <span class="mobile-menu__divider" aria-hidden="true"></span>
                    }
                  }
                </nav>
              </div>
            }
            @if (mobileMenuView === 'categories') {
              <div class="mobile-menu__panel">
                <div class="mobile-menu__category-header">
                  <button type="button" class="mobile-menu__back" (click)="closeMobileCategoryMenu()">
                    <svg class="mobile-menu__back-icon" xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
                      <path d="M8 16L0 8L8 0L9.41667 1.41667L2.83333 8L9.41667 14.5833L8 16Z" fill="#020617"/>
                    </svg>
                  </button>
                  <span class="mobile-menu__category-title">Shop by category</span>
                </div>
                <div class="mobile-menu__search">
                  <input
                    pInputText
                    [(ngModel)]="mobileCategorySearch"
                    class="mobile-menu__search-input"
                    type="text"
                    placeholder=""
                    aria-label="Search categories"
                    />
                  <svg class="mobile-menu__search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M11.5 11.5L14 14" stroke="#64748B" stroke-width="1.5" stroke-linecap="round"/>
                    <circle cx="7" cy="7" r="5" stroke="#64748B" stroke-width="1.5"/>
                  </svg>
                </div>
                <div class="mobile-menu__category-list">
                  @for (option of filteredMobileCategoryOptions; track option.value) {
                    <button type="button" class="mobile-menu__category" (click)="selectMobileCategory(option.value)">
                      <span class="mobile-menu__category-label">{{ option.label }}</span>
                    </button>
                  }
                </div>
              </div>
            }
          </div>
          <ng-template #mobileMenuArrow>
            <svg class="mobile-menu__arrow" xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
              <path d="M1.41667 16L0 14.5833L6.58333 8L0 1.41667L1.41667 0L9.41667 8L1.41667 16Z" fill="#020617"/>
            </svg>
          </ng-template>
        </ng-template>
      </p-drawer>

      <p-drawer
        id="mobile-account-drawer"
        [(visible)]="mobileAccountOpen"
        position="right"
        [modal]="false"
        [dismissible]="false"
        [blockScroll]="false"
        [showCloseIcon]="false"
        [motionOptions]="mobileDrawerMotionOptions"
        styleClass="mobile-account-drawer"
        (onHide)="handleMobileAccountHide()"
      >
        <ng-template pTemplate="headless">
          <div class="mobile-account-menu">
            <div class="mobile-account-menu__panel">
              @if (mobileAccountView === 'main') {
                <nav class="mobile-account-menu__nav" aria-label="Account menu">
                  @for (link of mobileAccountLinks; track link.label) {
                    <button
                      type="button"
                      class="mobile-account-menu__link"
                      [class.mobile-account-menu__link--spaced]="link.view === 'notifications'"
                      (click)="openMobileAccountSection(link.view)"
                    >
                      <span class="mobile-account-menu__label">{{ link.label }}</span>
                      <svg class="mobile-account-menu__arrow" xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
                        <path d="M1.41667 16L0 14.5833L6.58333 8L0 1.41667L1.41667 0L9.41667 8L1.41667 16Z" fill="#020617"/>
                      </svg>
                    </button>
                    @if (link.divider) {
                      <span class="mobile-account-menu__divider" aria-hidden="true"></span>
                    }
                  }
                </nav>
                <button type="button" class="mobile-account-menu__signout" (click)="handleMobileAccountSignOut()">
                  Sign Out
                </button>
              } @else {
                <div class="mobile-account-menu__content">
                  <div class="mobile-account-menu__header">
                    <button type="button" class="mobile-account-menu__back" (click)="handleMobileAccountBack()">
                      <svg class="mobile-account-menu__back-icon" xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
                        <path d="M8 16L0 8L8 0L9.41667 1.41667L2.83333 8L9.41667 14.5833L8 16Z" fill="#020617"/>
                      </svg>
                    </button>
                    <span class="mobile-account-menu__title">{{ mobileAccountTitle }}</span>
                  </div>
                  <div class="mobile-account-menu__body">
                    @if (mobileAccountLoading) {
                      <mat-spinner class="mx-auto mt-2" [diameter]="24" [strokeWidth]="2"></mat-spinner>
                    }
                    @if (!mobileAccountLoading) {
                      @if (mobileAccountView === 'notifications' || mobileAccountView === 'personal' || mobileAccountView === 'password' || mobileAccountView === 'payments') {
                        @if (mobileAccountUser && mobileAccountAuthUser) {
                          @if (mobileAccountView === 'notifications') {
                            @defer (when mobileAccountView === 'notifications') {
                              <app-profile-notifications [publicUser]="mobileAccountUser"></app-profile-notifications>
                            } @placeholder {
                              <mat-spinner class="mx-auto mt-2" [diameter]="24" [strokeWidth]="2"></mat-spinner>
                            }
                          }
                          @if (mobileAccountView === 'personal') {
                            @defer (when mobileAccountView === 'personal') {
                              <app-profile-personal-info
                                #personalInfoPanel
                                [publicUser]="mobileAccountUser"
                                [user]="mobileAccountAuthUser"
                                (titleChange)="onMobilePersonalInfoTitleChange($event)"
                                (requestDrawerClose)="closeMobileAccountMenu()"
                              ></app-profile-personal-info>
                            } @placeholder {
                              <mat-spinner class="mx-auto mt-2" [diameter]="24" [strokeWidth]="2"></mat-spinner>
                            }
                          }
                          @if (mobileAccountView === 'password') {
                            @defer (when mobileAccountView === 'password') {
                              <app-profile-change-password
                                [publicUser]="mobileAccountUser"
                              ></app-profile-change-password>
                            } @placeholder {
                              <mat-spinner class="mx-auto mt-2" [diameter]="24" [strokeWidth]="2"></mat-spinner>
                            }
                          }
                          @if (mobileAccountView === 'payments') {
                            @defer (when mobileAccountView === 'payments') {
                              <app-profile-payment-methods
                                #paymentMethodsPanel
                                [publicUser]="mobileAccountUser"
                                [user]="mobileAccountAuthUser"
                                (titleChange)="onMobilePaymentMethodsTitleChange($event)"
                              ></app-profile-payment-methods>
                            } @placeholder {
                              <mat-spinner class="mx-auto mt-2" [diameter]="24" [strokeWidth]="2"></mat-spinner>
                            }
                          }
                        } @else {
                          <p class="mobile-account-menu__empty">Unable to load account details.</p>
                        }
                      }
                      @if (mobileAccountView === 'invoices') {
                        @defer (when mobileAccountView === 'invoices') {
                          <app-profile-invoices></app-profile-invoices>
                        } @placeholder {
                          <mat-spinner class="mx-auto mt-2" [diameter]="24" [strokeWidth]="2"></mat-spinner>
                        }
                      }
                      @if (mobileAccountView === 'credits') {
                        @defer (when mobileAccountView === 'credits') {
                          <app-credits></app-credits>
                        } @placeholder {
                          <mat-spinner class="mx-auto mt-2" [diameter]="24" [strokeWidth]="2"></mat-spinner>
                        }
                      }
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        </ng-template>
      </p-drawer>
      }
    `
})
export class HeaderComponent implements OnDestroy {
  private static readonly HISTORY_STORAGE_KEY = 'header_search_history';
  private static readonly HISTORY_DISPLAY_LIMIT = 4;
  private static readonly HISTORY_STORAGE_LIMIT = 20;
  private static readonly HISTORY_VALUE_MAX_LENGTH = 200;
  private static readonly CATEGORY_DISPLAY_LIMIT = 7;
  private static readonly MOBILE_BREAKPOINT = 900;
  /** Whether the user is logged in. */
  @Input() loggedIn = false;
  /** Whether auth state has been resolved. */
  @Input() authLoaded = false;
  /** Display name shown when logged in. */
  @Input() userName = '';
  @Input() header? = '';
  @Input() sid = '';
  /** Current search input value. */
  @Input() searchTerm = '';
  /** Shared brand assets for the header. */
  public readonly brand = RASMUS_BRANDING.logo;
  /** Mobile menu links displayed inside the drawer. */
  public readonly mobileMenuLinks: Array<{ label: string; routerLink?: string; href?: string; divider?: boolean }> = [
    { label: 'Sell Real Estate', routerLink: '/real-estate' },
    { label: 'Rasmus TV', routerLink: '/rasmus-tv' },
    { label: 'Sell on Rasmus', routerLink: '/sellers' },
    { label: 'For Sellers', routerLink: '/for-sellers' },
    { label: 'Buyers', routerLink: '/buyers' },
    { label: 'Get Alerts', routerLink: '/sms-signup' },
    { label: 'About us', routerLink: '/about-us', divider: true },
    { label: 'Blog', routerLink: '/blog' },
    { label: 'Support', routerLink: '/knowledge-base' },
    { label: 'Help', routerLink: '/knowledge-base' },
    { label: 'Contact Us', routerLink: '/contact', divider: true },
  ];
  /** Mobile account links displayed inside the account drawer. */
  public readonly mobileAccountLinks: Array<{ label: string; view: MobileAccountView; divider?: boolean }> = [
    { label: 'Notifications', view: 'notifications', divider: true },
    { label: 'Personal information', view: 'personal' },
    { label: 'Change password', view: 'password' },
    { label: 'Payment methods', view: 'payments' },
    { label: 'Invoices', view: 'invoices' },
    { label: 'Credits', view: 'credits' },
  ];
  /** Controls visibility of the mobile menu drawer. */
  public mobileMenuOpen = false;
  /** Motion timing used by mobile drawers for immediate reopen and close response. */
  public readonly mobileDrawerMotionOptions = {
    duration: {
      enter: MOBILE_DRAWER_RESPONSE_MS,
      leave: MOBILE_DRAWER_RESPONSE_MS,
    },
  };
  /** Controls visibility of the mobile account drawer. */
  public mobileAccountOpen = false;
  /** Current view state of the mobile account menu. */
  public mobileAccountView: MobileAccountView = 'main';
  public mobileAccountLoading = false;
  public mobileAccountUser?: IUser;
  public mobileAccountAuthUser?: User;
  public mobileAccountTitleOverride = '';

  @ViewChild('personalInfoPanel')
  private personalInfoComponent?: { resetToOverview?: () => void };
  @ViewChild('paymentMethodsPanel')
  private paymentMethodsComponent?: {
    handleMobileBack?: () => boolean;
    resetToOverview?: () => void;
  };
  /** Current view state of the mobile menu. */
  public mobileMenuView: 'main' | 'categories' = 'main';
  /** Category options shown in the mobile category view. */
  public mobileCategoryOptions: Array<{ label: string; value: string }> = [];
  /** Search input value for filtering mobile categories. */
  public mobileCategorySearch = '';
  /** Emit when the sign in button is clicked. */
  signInClick = output<void>();
  /** Emit when the search is submitted. */
  searchSubmit = output<string>();
  /** Emit when the user chooses to log out. */
  logout = output<void>();
  /** Controls the visibility of the autocomplete panel. */
  public autocompleteOpen = false;
  /** Controls the mobile fullscreen search overlay. */
  public mobileSearchOpen = false;
  /** Recent search history, most recent first. */
  public searchHistory: RecentSearchEntry[] = [];
  /** Filtered history based on the current search term. */
  public filteredSearchHistory: RecentSearchEntry[] = [];
  /** Recommended auction titles for autocomplete. */
  public auctionTitleRecommendations: string[] = [];
  /** Filtered auction titles based on the current search term. */
  public filteredAuctionTitles: string[] = [];
  /** Filtered category options based on the current search term. */
  public filteredCategoryOptions: Array<{ label: string; value: string }> = [];
  public hostname = '';
  /** Whether the user has typed a non-empty search term. */
  public get hasTypedSearch(): boolean {
    return (this.searchTerm || '').trim().length > 0;
  }
  /** Whether there are suggestions to show in the autocomplete panel. */
  public get shouldShowAutocomplete(): boolean {
    const hasHistory = this.filteredSearchHistory.length > 0;
    const hasTypedSuggestions = this.hasTypedSearch && (this.filteredAuctionTitles.length > 0 || this.filteredCategoryOptions.length > 0);
    return hasHistory || hasTypedSuggestions;
  }
  /** Lazily-resolved white-label header for the active brand (see WhiteLabelHeaderLoader). */
  private readonly whiteLabelHeaderLoader = inject(WhiteLabelHeaderLoader);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private readonly auctionService = inject(AuctionService);
  private readonly errorService = inject(ErrorService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly cardTransitions = inject(LotViewTransitionService);
  private readonly cdr = inject(ChangeDetectorRef);
  /** Search term queued for debounced recommendation loading. */
  private readonly recommendationSearchTerm = signal('');
  private auctionTitlesLoading = false;
  private auctionTitleRequestId = 0;
  private lastAuctionQuery = '';
  /** Cancels the latest auction title request to prevent stale updates. */
  private cancelAuctionTitleRequest?: () => void;
  /** Whether the component has been destroyed. */
  private isDestroyed = false;

  constructor() {
    this.hostname = this.document?.location?.hostname;
    consumeSignalEvents(this.auctionService.headerCategoryOptions, (options) => {
      this.mobileCategoryOptions = options ?? [];
      this.filteredCategoryOptions = this.filterCategoryOptions(this.searchTerm);
      this.cdr.markForCheck();
    });
    effect((onCleanup) => {
      const term = this.recommendationSearchTerm();
      const timer = setTimeout(() => this.updateAuctionTitleRecommendations(term), 300);
      onCleanup(() => clearTimeout(timer));
    });
    this.loadSearchHistory();
  }

  /** The matching white-label header component for the current site, or null for the default Rasmus header. */
  public get whiteLabelComponent(): Type<unknown> | null {
    return this.whiteLabelHeaderLoader.component();
  }

  /** Notify parent when search is submitted. */
  public onSearchSubmit(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    const trimmed = (this.searchTerm || '').trim();
    const sanitized = this.sanitizeInput(trimmed);
    this.searchTerm = sanitized;
    this.addSearchHistoryEntry('text', sanitized);
    this.filteredSearchHistory = this.filterSearchHistory(this.searchTerm);
    this.searchSubmit.emit(sanitized);
    this.autocompleteOpen = false;
    this.mobileSearchOpen = false;
  }

  /** Show the autocomplete panel when the search input is focused. */
  public openAutocomplete(): void {
    this.autocompleteOpen = true;
    this.filteredSearchHistory = this.filterSearchHistory(this.searchTerm);
    this.updateAuctionTitleRecommendations(this.searchTerm);
    this.filteredCategoryOptions = this.filterCategoryOptions(this.searchTerm);
  }

  /** Hide the autocomplete panel when the search input loses focus. */
  public closeAutocomplete(): void {
    this.autocompleteOpen = false;
  }

  /** Open the fullscreen search experience on mobile viewports. */
  public openMobileSearch(): void {
    this.mobileSearchOpen = true;
    this.autocompleteOpen = false;
    this.filteredSearchHistory = this.filterSearchHistory(this.searchTerm);
    this.updateAuctionTitleRecommendations(this.searchTerm);
    this.filteredCategoryOptions = this.filterCategoryOptions(this.searchTerm);
  }

  /** Close the fullscreen search experience on mobile viewports. */
  public closeMobileSearch(): void {
    this.mobileSearchOpen = false;
  }

  /** Handle focus or click on the header search input. */
  public handleSearchFocus(): void {
    if (this.shouldUseMobileSearch()) {
      this.openMobileSearch();
      return;
    }
    this.openAutocomplete();
  }

  /** Keep the input focused when interacting with autocomplete options. */
  public keepAutocompleteOpen(event: MouseEvent): void {
    event.preventDefault();
  }

  /** Update autocomplete options while the user types. */
  public onSearchInput(): void {
    this.filteredSearchHistory = this.filterSearchHistory(this.searchTerm);
    this.recommendationSearchTerm.set(this.searchTerm);
    this.filteredCategoryOptions = this.filterCategoryOptions(this.searchTerm);
  }

  /**
   * Applies a selected history term and submits the search.
   * @param term - Selected history entry
   */
  public applyHistoryTerm(term: RecentSearchEntry): void {
    if (term.type === 'category') {
      this.applyCategorySelection(term.value);
      return;
    }
    this.searchTerm = term.value;
    this.onSearchSubmit();
    this.autocompleteOpen = false;
    this.mobileSearchOpen = false;
  }

  /**
   * Applies a text search value from autocomplete and submits the search.
   * @param value - Selected text value
   */
  public applyTextSearch(value: string): void {
    const entry = this.buildSearchHistoryEntry('text', value);
    if (!entry) {
      return;
    }
    this.searchTerm = entry.value;
    this.onSearchSubmit();
    this.autocompleteOpen = false;
    this.mobileSearchOpen = false;
  }

  /**
   * Applies a category selection from autocomplete.
   * @param category - Selected category value
   */
  public applyCategorySelection(category: string): void {
    this.searchTerm = '';
    this.filteredSearchHistory = this.filterSearchHistory(this.searchTerm);
    this.filteredAuctionTitles = [];
    this.filteredCategoryOptions = this.filterCategoryOptions(this.searchTerm);
    this.navigateToCategory(category, () => {
      this.autocompleteOpen = false;
      this.mobileSearchOpen = false;
    });
  }

  /** Notify parent the sign in button was clicked. */
  public onSignInClick(): void {
    this.signInClick.emit();
  }

  /**
   * Handles logo clicks by navigating home to restore the hero carousel.
   * @param event - Mouse event from logo click
   */
  public onLogoClick(event: MouseEvent): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    const match = this.router.url.split(/[?#]/, 1)[0].match(/^\/auctions\/([^/]+)/);
    const aid = match?.[1] ? decodeURIComponent(match[1]) : '';
    if (!aid) {
      void this.router.navigate(['/']);
      return;
    }
    void this.cardTransitions
      .runRouterNavigation(
        { kind: 'auction', id: aid },
        () => this.router.navigate(['/']),
        { mode: 'page-fade' },
      )
      .catch(() => undefined);
  }

  /** Opens the mobile drawer menu. */
  public openMobileMenu(): void {
    this.mobileAccountOpen = false;
    this.mobileMenuOpen = true;
    this.resetMobileMenuState();
  }

  /** Closes the mobile drawer menu. */
  public closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.resetMobileMenuState();
  }

  /** Syncs state after PrimeNG finishes hiding the mobile drawer. */
  public handleMobileMenuHide(): void {
    if (this.mobileMenuOpen) {
      return;
    }
    this.resetMobileMenuState();
  }

  /** Opens the mobile account drawer menu. */
  public openMobileAccountMenu(): void {
    this.mobileMenuOpen = false;
    this.resetMobileMenuState();
    this.mobileAccountOpen = true;
    this.mobileAccountView = 'main';
    this.mobileAccountTitleOverride = '';
    void this.loadMobileAccountProfile();
  }

  /** Closes the mobile account drawer menu. */
  public closeMobileAccountMenu(): void {
    this.mobileAccountOpen = false;
    this.mobileAccountView = 'main';
    this.mobileAccountTitleOverride = '';
  }

  /** Syncs state after PrimeNG finishes hiding the account drawer. */
  public handleMobileAccountHide(): void {
    if (this.mobileAccountOpen) {
      return;
    }
    this.mobileAccountView = 'main';
    this.mobileAccountTitleOverride = '';
  }

  /**
   * Switches the mobile account drawer to the selected view.
   * @param view - selected account view
   */
  public openMobileAccountSection(view: MobileAccountView): void {
    this.mobileAccountView = view;
    this.mobileAccountTitleOverride = '';
    void this.loadMobileAccountProfile();
  }

  /** Returns to the main mobile account menu view. */
  public closeMobileAccountSection(): void {
    if (this.mobileAccountView === 'payments') {
      this.paymentMethodsComponent?.resetToOverview?.();
    }
    this.mobileAccountView = 'main';
    this.mobileAccountTitleOverride = '';
  }

  /**
   * Emits sign out and closes the mobile account drawer.
   */
  public handleMobileAccountSignOut(): void {
    this.closeMobileAccountMenu();
    this.logout.emit();
  }

  /** Handles the back action for the mobile account drawer. */
  public handleMobileAccountBack(): void {
    if (this.mobileAccountView === 'personal' && this.mobileAccountTitle !== 'Personal information') {
      this.personalInfoComponent?.resetToOverview?.();
      this.mobileAccountTitleOverride = 'Personal information';
      this.cdr.markForCheck();
      return;
    }
    if (this.mobileAccountView === 'payments' && this.paymentMethodsComponent?.handleMobileBack?.()) {
      this.cdr.markForCheck();
      return;
    }
    this.closeMobileAccountSection();
  }

  /** Current title for the mobile account content header. */
  public get mobileAccountTitle(): string {
    if (this.mobileAccountTitleOverride && (this.mobileAccountView === 'personal' || this.mobileAccountView === 'payments')) {
      return this.mobileAccountTitleOverride;
    }
    switch (this.mobileAccountView) {
      case 'notifications':
        return 'Notifications';
      case 'personal':
        return 'Personal information';
      case 'password':
        return 'Change password';
      case 'payments':
        return 'Payment methods';
      case 'invoices':
        return 'Invoices';
      case 'credits':
        return 'Credits';
      default:
        return '';
    }
  }

  /**
   * Sets the mobile account drawer title for the personal info flow.
   * @param title - title emitted by the personal info component
   */
  public onMobilePersonalInfoTitleChange(title: string): void {
    this.mobileAccountTitleOverride = title;
    this.cdr.markForCheck();
  }

  /**
   * Sets the mobile account drawer title for the payment methods flow.
   * @param title - title emitted by the payment methods component
   */
  public onMobilePaymentMethodsTitleChange(title: string): void {
    this.mobileAccountTitleOverride = title;
    this.cdr.markForCheck();
  }

  /** Handles the account row click for logged-out mobile users. */
  public handleMobileAccountClick(): void {
    this.onSignInClick();
    this.closeMobileMenu();
  }

  /** Opens the category list inside the mobile menu. */
  public openMobileCategoryMenu(): void {
    this.mobileMenuView = 'categories';
    this.mobileCategorySearch = '';
  }

  /**
   * Resets mobile menu state to defaults.
   */
  private resetMobileMenuState(): void {
    this.mobileMenuView = 'main';
    this.mobileCategorySearch = '';
  }

  /** Returns to the main mobile menu view. */
  public closeMobileCategoryMenu(): void {
    this.mobileMenuView = 'main';
    this.mobileCategorySearch = '';
  }

  /** Filtered category list for the mobile menu. */
  public get filteredMobileCategoryOptions(): Array<{ label: string; value: string }> {
    const term = this.mobileCategorySearch.trim().toLowerCase();
    if (!term) {
      return this.mobileCategoryOptions;
    }
    return this.mobileCategoryOptions.filter((option) => option.label.toLowerCase().includes(term));
  }

  /**
   * Handles selecting a category from the mobile menu list.
   * @param category selected category value
   */
  public selectMobileCategory(category: string): void {
    this.navigateToCategory(category, () => this.closeMobileMenu());
  }

  /** Cleanup subscriptions on destroy. */
  public ngOnDestroy(): void {
    this.isDestroyed = true;
    this.cancelAuctionTitleRequest?.();
  }

  /**
   * Navigates to the search route with the selected category.
   * @param category - Selected category value
   * @param onComplete - Callback invoked after navigation completes
   */
  private navigateToCategory(category: string, onComplete: () => void): void {
    const emitSelection = () => this.auctionService.headerCategorySelection.emit(category);
    const navigationTarget = ['/search'];
    this.router
      .navigate(navigationTarget, { queryParams: { [QUERY_PARAMS.CATEGORY]: category }, queryParamsHandling: 'merge' })
      .then(() => {
        this.addSearchHistoryEntry('category', category);
        emitSelection();
      })
      .catch((error) => {
        if (shouldLog()) {
          console.error('Navigation failed:', error);
        }
        this.errorService.showError('Unable to navigate to category');
      })
      .finally(() => {
        onComplete();
        this.cdr.markForCheck();
      });
  }

  /** Load stored search history from local storage when in the browser. */
  private loadSearchHistory(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      if (typeof window.localStorage === 'undefined') {
        return;
      }
      const raw = window.localStorage.getItem(HeaderComponent.HISTORY_STORAGE_KEY);
      const entries = this.parseStoredHistory(raw);
      this.searchHistory = entries;
      this.filteredSearchHistory = this.filterSearchHistory(this.searchTerm);
    } catch (error) {
      if (shouldLog()) {
        console.error('Failed to load search history:', error);
      }
      this.errorService.showError('Unable to load search history');
      this.searchHistory = [];
      this.filteredSearchHistory = [];
    }
  }

  /** Persist the current search history to local storage when available. */
  private persistSearchHistory(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      window.localStorage.setItem(
        HeaderComponent.HISTORY_STORAGE_KEY,
        JSON.stringify(this.searchHistory),
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.searchHistory = this.searchHistory.slice(0, 10);
        try {
          window.localStorage.setItem(
            HeaderComponent.HISTORY_STORAGE_KEY,
            JSON.stringify(this.searchHistory),
          );
          return;
        } catch (retryError) {
          this.searchHistory = [];
          if (shouldLog()) {
            console.error('Failed to persist search history after trimming:', retryError);
          }
          this.errorService.showError('Unable to save search history');
          return;
        }
      }
      if (shouldLog()) {
        console.error('Failed to persist search history:', error);
      }
      this.errorService.showError('Unable to save search history');
    }
  }

  /**
   * Adds a search entry to history while keeping it within the limit.
   * @param type - Entry type to store
   * @param value - Entry value to store
   */
  private addSearchHistoryEntry(type: RecentSearchType, value: string): void {
    const entry = this.buildSearchHistoryEntry(type, value);
    if (!entry) {
      return;
    }
    const key = this.getSearchHistoryKey(entry);
    const next = [entry, ...this.searchHistory.filter((item) => this.getSearchHistoryKey(item) !== key)];
    this.searchHistory = next.slice(0, HeaderComponent.HISTORY_STORAGE_LIMIT);
    this.persistSearchHistory();
  }

  /**
   * Filters search history using a case-insensitive includes match.
   * @param term - Current search input
   * @returns Filtered history list
   */
  private filterSearchHistory(term: string): RecentSearchEntry[] {
    const query = (term || '').trim().toLowerCase();
    if (!query) {
      return this.searchHistory.slice(0, HeaderComponent.HISTORY_DISPLAY_LIMIT);
    }
    return this.searchHistory
      .filter((value) => value.value.toLowerCase().includes(query))
      .slice(0, HeaderComponent.HISTORY_DISPLAY_LIMIT);
  }

  /**
   * Filters category options using a case-insensitive match.
   * @param term - Current search input
   * @returns Filtered category options
   */
  private filterCategoryOptions(term: string): Array<{ label: string; value: string }> {
    const query = (term || '').trim().toLowerCase();
    const options = this.mobileCategoryOptions ?? [];
    if (!query) {
      return [];
    }
    return options
      .filter((option) => option.label.toLowerCase().includes(query))
      .slice(0, HeaderComponent.CATEGORY_DISPLAY_LIMIT);
  }

  /**
   * Loads open auction title recommendations for the current search query.
   * @param term - Current search input
   */
  private updateAuctionTitleRecommendations(term: string): void {
    if (this.isDestroyed) {
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const query = this.sanitizeInput((term || '').trim());
    if (query !== this.lastAuctionQuery) {
      this.lastAuctionQuery = query;
      this.auctionTitleRecommendations = [];
      this.filteredAuctionTitles = [];
      this.cdr.markForCheck();
    }
    if (!query) {
      this.auctionTitlesLoading = false;
      return;
    }
    this.auctionTitlesLoading = true;
    const requestId = ++this.auctionTitleRequestId;
    let cancelled = false;
    this.cancelAuctionTitleRequest = () => {
      cancelled = true;
    };
    void this.auctionService
      .getOpenAuctions({
        filters: [],
        facetFilters: [],
        mode: 'open_only',
        sort: 'asc',
        page: 0,
        query,
      })
      .then((results) => {
        if (this.isDestroyed || cancelled || requestId !== this.auctionTitleRequestId) {
          return;
        }
        const rawTitles = (results?.hits ?? [])
          .map((auction) => (auction.name || auction.headline || '').trim())
          .filter(Boolean);
        this.auctionTitleRecommendations = this.uniqueTitles(rawTitles);
        this.filteredAuctionTitles = this.auctionTitleRecommendations.slice(
          0,
          HeaderComponent.HISTORY_DISPLAY_LIMIT,
        );
        this.auctionTitlesLoading = false;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        if (this.isDestroyed || cancelled || requestId !== this.auctionTitleRequestId) {
          return;
        }
        if (shouldLog()) {
          console.error('Failed to load auction titles:', error);
        }
        this.errorService.showError('Unable to load auction titles');
        this.auctionTitlesLoading = false;
      })
      .finally(() => {
        if (requestId === this.auctionTitleRequestId) {
          this.cancelAuctionTitleRequest = undefined;
        }
      });
  }

  /**
   * Removes duplicate titles while preserving the original order.
   * @param titles - Auction titles to normalize
   * @returns Unique auction titles
   */
  private uniqueTitles(titles: string[]): string[] {
    const seen = new Set<string>();
    const output: string[] = [];
    titles.forEach((title) => {
      const key = title.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      output.push(title);
    });
    return output;
  }

  /**
   * Reads and normalizes search history entries from local storage.
   * @param raw - Raw local storage payload
   * @returns Normalized search history entries
   */
  private parseStoredHistory(raw: string | null): RecentSearchEntry[] {
    if (!raw) {
      return [];
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      if (shouldLog()) {
        console.error('Failed to parse search history:', error);
      }
      this.errorService.showError('Unable to load search history');
      return [];
    }
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((entry) => this.normalizeSearchHistoryEntry(entry))
      .filter((entry): entry is RecentSearchEntry => Boolean(entry));
  }

  /**
   * Converts persisted history values into normalized entries.
   * @param entry - Stored entry candidate
   * @returns Normalized entry or null when invalid
   */
  private normalizeSearchHistoryEntry(entry: unknown): RecentSearchEntry | null {
    if (typeof entry === 'string') {
      return this.buildSearchHistoryEntry('text', entry);
    }
    if (!entry || typeof entry !== 'object') {
      return null;
    }
    const candidate = entry as { type?: unknown; value?: unknown };
    if (typeof candidate.value !== 'string') {
      return null;
    }
    const normalizedType: RecentSearchType = candidate.type === 'category' ? 'category' : 'text';
    return this.buildSearchHistoryEntry(normalizedType, candidate.value);
  }

  /**
   * Sanitizes and constructs a search history entry.
   * @param type - Entry type
   * @param value - Entry value
   * @returns Sanitized entry or null when empty
   */
  private buildSearchHistoryEntry(type: RecentSearchType, value: string): RecentSearchEntry | null {
    const normalizedValue = this.sanitizeInput((value || '').trim());
    if (!normalizedValue || normalizedValue.length > HeaderComponent.HISTORY_VALUE_MAX_LENGTH) {
      return null;
    }
    return { type, value: normalizedValue };
  }

  /** Builds a stable track key for search history entries. */
  public getSearchHistoryKey(entry: RecentSearchEntry): string {
    return `${entry.type}:${entry.value.toLowerCase()}`;
  }

  /**
   * Builds a stable track key for history entries used by the template.
   * @param index - Render index
   * @param entry - History entry
   * @returns Stable tracking key
   */
  public trackByHistoryEntry(index: number, entry: RecentSearchEntry): string {
    return this.getSearchHistoryKey(entry);
  }

  /**
   * Note: Angular sanitizes template bindings automatically.
   * This additional sanitization is for search history storage only.
   * @param text user-provided search text
   * @returns sanitized text safe to render in the UI
   */
  private sanitizeInput(text: string): string {
    return text.replace(/[<>]/g, '');
  }

  /** Determines whether the mobile search overlay should be used. */
  private shouldUseMobileSearch(): boolean {
    return this.getViewportWidth() <= HeaderComponent.MOBILE_BREAKPOINT;
  }

  /** Determines whether the header should render mobile controls. */
  public isMobileViewport(): boolean {
    return this.getViewportWidth() <= HeaderComponent.MOBILE_BREAKPOINT;
  }

  /**
   * Returns the active viewport width or 0 during SSR.
   */
  private getViewportWidth(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 0;
    }
    return this.document?.defaultView?.innerWidth ?? 0;
  }

  /**
   * Loads the authenticated user and profile for the mobile account drawer.
   */
  private async loadMobileAccountProfile(): Promise<void> {
    if (this.mobileAccountLoading || this.mobileAccountUser) {
      return;
    }
    const authUser = this.authService.user();
    if (!authUser) {
      return;
    }
    this.mobileAccountLoading = true;
    this.mobileAccountAuthUser = authUser;
    try {
      this.mobileAccountUser = await this.userService.profile();
    } catch (error) {
      if (shouldLog()) {
        console.warn('[header] failed to load mobile account profile', error);
      }
      this.errorService.showError('Unable to load account profile');
    } finally {
      this.mobileAccountLoading = false;
      this.cdr.markForCheck();
    }
  }
}
